import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import HomeIcon from "@mui/icons-material/Home";
import PublicIcon from "@mui/icons-material/Public";
import Cookies from "universal-cookie";
import GooglePlaceAutocomplete from "../GooglePlaceAutocomplete";
import ContinueButton from "./ContinueButton";
import { brandColors } from "../brandedComponent/BrandedColors";
import { LifeSafer } from "../../_helpers/BrandConstants";
import { utilities } from "@/_helpers";
import { getRegionId, getRegions } from "../../_api/dataListApi";
import { useToaster } from "../toaster/MuiToasterProvider";

const getComponent = (components, type, short = false) => {
  const component = components.find((item) => item.types?.includes(type));

  return short ? component?.short_name || "" : component?.long_name || "";
};

const EuropeAddressInfo = ({ goToBackStep, submitting, handleUpdate }) => {
  const { t } = useTranslation();
  const toast = useToaster();

  const cookies = new Cookies();
  const brand = cookies.get("lsEnrollmentReferral") || LifeSafer;

  const enrolleeUser = JSON.parse(sessionStorage.getItem("enrolleeUser")) || {};

  const savedHomeAddr1 =
    enrolleeUser.homeAddr1 ||
    enrolleeUser.HomeAddr1 ||
    enrolleeUser.homeAddress1 ||
    enrolleeUser.HomeAddress1 ||
    enrolleeUser.address1 ||
    "";
  const savedHomeAddr2 =
    enrolleeUser.homeAddr2 ||
    enrolleeUser.HomeAddr2 ||
    enrolleeUser.homeAddress2 ||
    enrolleeUser.HomeAddress2 ||
    enrolleeUser.address2 ||
    "";
  const savedCity = enrolleeUser.city || enrolleeUser.City || "";
  const savedProvince =
    enrolleeUser.state ||
    enrolleeUser.State ||
    enrolleeUser.province ||
    enrolleeUser.Province ||
    "";
  const savedZip =
    enrolleeUser.zip ||
    enrolleeUser.Zip ||
    enrolleeUser.postalCode ||
    enrolleeUser.PostalCode ||
    "";

  // const savedCountry = enrolleeUser.country || enrolleeUser.Country;
  const savedCountry=enrolleeUser?.homeAddrCountry;

  const savedCountryCode =
    enrolleeUser.homeAddressCountryCode || enrolleeUser.homeAddressCountryCode || savedCountry;

  const [europeanCountries, setEuropeanCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [country, setCountry] = useState(null);
  const [countryError, setCountryError] = useState(false);
  const [addressFromAutocomplete, setAddressFromAutocomplete] = useState(
    Boolean(enrolleeUser.addressFromAutocomplete && savedCountry),
  );
  const [coordinates, setCoordinates] = useState({
    lat: null,
    lng: null,
  });

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      homeAddr1: savedHomeAddr1,
      homeAddr2: savedHomeAddr2,
      zip: savedZip,
      city: savedCity,
      province: savedProvince,
    },
  });

  useEffect(() => {
    let isActive = true;

    setCountriesLoading(true);

    getRegionId({
      regionTypeId: 7,
      description: "European Union",
    })
      .then((region) => {
        const regionId =
          typeof region === "number"
            ? region
            : (region?.regionId ?? region?.RegionId);

        if (!regionId) {
          throw new Error("European Union region ID was not returned.");
        }

        return getRegions(regionId);
      })
      .then((regionsResponse) => {
        if (!isActive) return;

        const countriesResponse = Array.isArray(regionsResponse)
          ? regionsResponse
          : (regionsResponse?.countries ?? regionsResponse?.Countries ?? []);

        const countries = countriesResponse
          .map((item) => ({
            label: item?.description ?? item?.Description ?? "",

            code: item?.code ?? item?.Code ?? "",

            countryId: item?.countryId ?? item?.CountryId,

            regionId: item?.regionId ?? item?.RegionId,
          }))
          .filter((item) => item.label && item.code)
          .sort((first, second) => first.label.localeCompare(second.label));

        setEuropeanCountries(countries);

        const normalizedSavedCountry = String(savedCountry || "").toLowerCase();

        const normalizedSavedCode = String(
          savedCountryCode || "",
        ).toLowerCase();

        const matchedCountry = countries.find(
          (item) =>
            item.label.toLowerCase() === normalizedSavedCountry ||
            item.code.toLowerCase() === normalizedSavedCode,
        );

        if (matchedCountry) {
          setCountry(matchedCountry);
        }
      })
      .catch(() => {
        if (isActive) {
          setEuropeanCountries([]);
        }
      })
      .finally(() => {
        if (isActive) {
          setCountriesLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [savedCountry, savedCountryCode]);

  const fieldIcon = {
    startAdornment: (
      <HomeIcon
        className="inputIco"
        style={{
          color: brandColors[brand].primary,
        }}
      />
    ),
  };

  const setAddressField = (name, value) => {
    setValue(name, value || "", {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const clearAutoFilledAddress = () => {
    setAddressField("city", "");
    setAddressField("province", "");
    setAddressField("zip", "");
    setCountry(null);
    setCountryError(true);
    setAddressFromAutocomplete(false);
    setCoordinates({ lat: null, lng: null });
  };

  const updateFromGoogle = (place) => {
    const components = utilities.normalizeAddressComponents(
      place?.address_components || [],
    );

    const street = [
      getComponent(components, "street_number"),
      getComponent(components, "route"),
    ]
      .filter(Boolean)
      .join(" ");

    const city =
      getComponent(components, "locality") ||
      getComponent(components, "postal_town") ||
      getComponent(components, "administrative_area_level_3");

    const province = getComponent(components, "administrative_area_level_1");

    const postalCode = getComponent(components, "postal_code");

    const countryCode = getComponent(components, "country", true);

    const countryName = getComponent(components, "country");

    const googleCountry = europeanCountries.find(
      (item) =>
        item.code.toLowerCase() === countryCode.toLowerCase() ||
        item.label.toLowerCase() === countryName.toLowerCase(),
    );

    setAddressField("homeAddr1", street || place?.formatted_address || "");
    setAddressField("city", city);
    setAddressField("province", province);
    setAddressField("zip", postalCode);
    setCountry(googleCountry || null);
    setCountryError(!googleCountry);
    setAddressFromAutocomplete(Boolean(googleCountry));

    setCoordinates({
      lat: place?.geometry?.location?.lat?.() ?? null,

      lng: place?.geometry?.location?.lng?.() ?? null,
    });
  };

  const required = (message) => ({
    required: {
      value: true,
      message,
    },

    validate: (value) => Boolean(value?.trim()) || message,
  });

  const onSubmit = (data) => {
    if (!country) {
      setCountryError(true);
      toast.error(
        t(
          "europe.completeRequiredFields",
          "Please complete all required fields before continuing.",
        ),
      );
      return;
    }

    handleUpdate("Address", {
      homeAddr1: data.homeAddr1.trim(),

      homeAddr2: enrolleeUser.homeAddr2 || null,

      zip: data.zip.trim(),

      city: data.city.trim(),

      province: data.province.trim(),

      state: data.province.trim(),

      county: data.province.trim(),

      country: country.code,

      countryCode: country.code,

      addressFromAutocomplete,

      homeLat: coordinates.lat ?? enrolleeUser.homeLat ?? null,

      homeLong: coordinates.lng ?? enrolleeUser.homeLong ?? null,

      Step: "Address",
    });
  };

  const onInvalid = () => {
    if (!country) setCountryError(true);
    toast.error(
      t(
        "europe.completeRequiredFields",
        "Please complete all required fields before continuing.",
      ),
    );
  };

  return (
    <form
      className="form"
      noValidate
      onSubmit={handleSubmit(onSubmit, onInvalid)}
      style={{
        width: "100%",
        maxWidth: 300,
        marginBottom: 40,
      }}
    >
      <Grid
        container
        justifyContent="center"
        spacing="25px"
        sx={{ mt: "25px" }}
      >
        <Grid
          style={{
            maxWidth: 300,
            width: "100%",
          }}
        >
          <GooglePlaceAutocomplete
            name="homeAddr1"
            control={control}
            size="small"
            startIcon={HomeIcon}
            rules={required(t("body.homeAddrRequired"))}
            errors={errors}
            label={t("body.homeAddr1Label")}
            placeholder={t("body.enterHomeAddress")}
            countryCodes={"eu"}
            disabled={submitting}
            manualMode={false}
            onChange={(value) => {
              setAddressField("homeAddr1", value);
              clearAutoFilledAddress();
            }}
            onPlaceSelected={updateFromGoogle}
            defaultValue={savedHomeAddr1}
            startIconColor={brandColors[brand].primary}
            startIconClassName="inputIco"
          />
        </Grid>

        <Grid
          style={{
            maxWidth: 300,
            width: "100%",
          }}
        >
          <TextField
            fullWidth
            size="small"
            label={t("europe.postalCode")}
            disabled={submitting}
            error={!!errors.zip}
            helperText={errors.zip?.message || ""}
            {...register("zip", {
              required: required(
                t("europe.fieldRequired", {
                  field: t("europe.postalCodeRequired"),
                  defaultValue: "Postal Code is required",
                }),
              ),
              validate: (value) =>
                /^(?!.*\s\s)[a-zA-Z0-9-][a-zA-Z0-9\s-]{1,6}[a-zA-Z0-9-]$/.test(
                  value,
                ) || t("europe.postalCodeInvalid"),
            })}
            slotProps={{
              inputLabel: { shrink: true },
            }}
            InputProps={fieldIcon}
          />
        </Grid>

        <Grid
          style={{
            maxWidth: 300,
            width: "100%",
          }}
        >
          <TextField
            fullWidth
            size="small"
            label={t("body.city", "City")}
            disabled={submitting}
            error={!!errors.city}
            helperText={errors.city?.message || ""}
            {...register(
              "city",
              required(
                t("europe.fieldRequired", {
                  field: t("body.city", "City"),
                  defaultValue: "City is required",
                }),
              ),
            )}
            InputProps={fieldIcon}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />
        </Grid>

        <Grid
          style={{
            maxWidth: 300,
            width: "100%",
          }}
        >
          <TextField
            fullWidth
            size="small"
            label={t("body.province", "Province Code")}
            disabled={submitting}
            error={!!errors.province}
            helperText={errors.province?.message || ""}
            {...register(
              "province",
              required(
                t("europe.fieldRequired", {
                  field: t("body.province", "Province Code"),
                  defaultValue: "Province Code is required",
                }),
              ),
            )}
            InputProps={fieldIcon}
          />
        </Grid>

        <Grid
          style={{
            maxWidth: 300,
            width: "100%",
          }}
        >
          <Autocomplete
            options={europeanCountries}
            value={country}
            loading={countriesLoading}
            disabled={
              submitting ||
              countriesLoading ||
              (addressFromAutocomplete && Boolean(country))
            }
            isOptionEqualToValue={(option, value) =>
              option.countryId === value.countryId
            }
            onChange={(_, value) => {
              setCountry(value);
              setCountryError(!value);
              setAddressFromAutocomplete(false);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t("body.country", "Country")}
                error={countryError && !country}
                helperText={
                  countryError && !country
                    ? t("europe.countryRequired", "Country is required")
                    : ""
                }
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <PublicIcon
                      className="inputIco"
                      style={{
                        color: brandColors[brand].primary,
                        marginLeft: "9px",
                      }}
                    />
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid
          sx={{
            width: "100%",
            mt: "25px",
          }}
        >
          <ContinueButton
            goToBackStep={goToBackStep}
            submitting={submitting}
            showForwardIcon={false}
          />
        </Grid>
      </Grid>
    </form>
  );
};

EuropeAddressInfo.propTypes = {
  goToBackStep: PropTypes.func,
  submitting: PropTypes.bool,
  handleUpdate: PropTypes.func.isRequired,
};

export default EuropeAddressInfo;
